"use client";

// Third-party Imports
import { createSlice, createAsyncThunk, current } from "@reduxjs/toolkit";
import { toast } from "react-toastify";

// Type Imports
import type { entityTeamsTypes } from "@/types/apps/EntityTeams";

// Server Imports
import { doGetTeams } from "@/app/server/actions";

const initialState: entityTeamsTypes = {
  teams: [],
  filteredTeams: [],
  loading: "idle" as string | "pending" as string | "succeeded" as string | "failed" as string,
  error: null,
  total: 0,
  lastErrorToastTime: 0,
} as any;

export const fetchTeams = createAsyncThunk<any, any, any>("manage_teams/fetch_teams", async (_, { rejectWithValue }) => {
  const response = await doGetTeams();

  // If response is null, it means a 500 error occurred
  if (response === null || response === undefined) {
    return rejectWithValue("Failed to load teams");
  }

  // Ensure response is an array
  const teams = Array.isArray(response) ? response : [];

  return {
    data: teams,
    filtered: teams,
    total: teams.length,
  };
});

export const entityManageTeamsSlice = createSlice({
  name: "manageTeams",
  initialState: initialState,
  reducers: {
    filterGeneral: (state, action) => {
      const { search } = action.payload;
      const currentState = current(state);
      let data = currentState.filteredTeams;

      if (search && search.length > 0) {
        data = data.filter((team: any) => {
          return team.name.toLowerCase().includes(search.toLowerCase());
        });
      }

      state.teams = data;
    },
  },
  extraReducers: builder => {
    builder.addCase(fetchTeams.pending, state => {
      state.loading = "pending";
    });
    builder.addCase(fetchTeams.rejected, (state, action) => {
      // Don't show error if the request was aborted
      if (action.meta.aborted) {
        state.loading = "idle";
        return;
      }

      state.loading = "failed";
      state.error = action.payload;

      // Only show toast if there's an actual error and enough time has passed since last toast
      // This prevents duplicates in React Strict Mode (500ms debounce)
      const now = Date.now();
      const timeSinceLastToast = now - ((state as any).lastErrorToastTime || 0);

      if (action.payload && timeSinceLastToast > 500) {
        (state as any).lastErrorToastTime = now;
        toast.error("Failed to load teams. Please try again.", {
          toastId: "manage-teams-load-error",
        });
      }
    });
    builder.addCase(fetchTeams.fulfilled, (state, action) => {
      state.teams = action.payload.filtered;
      state.filteredTeams = action.payload.data;
      state.total = action.payload.total;
      state.loading = "succeeded";
    });
  },
});

export const { filterGeneral } = entityManageTeamsSlice.actions;

export default entityManageTeamsSlice.reducer;
